import SourcingSellerPicker from '../../../components/sourcing-seller-picker'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Formik, Form } from 'formik'
import { Card, Alert, Button, FormText, Input, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import { TitlePage, RedirectButton } from 'components/atoms'
import { InputField, ThousandsInputField } from 'components/molecules'
import SalesDealsApi from 'api/admins/sales-deals-api'

const toIntOrNull = (v) => {
  if (v === null || v === undefined || v === '') return null
  const n = parseInt(v, 10)
  return Number.isFinite(n) ? n : null
}

const toNumberOrNull = (v) => {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

const getCreatedByText = (deal) => {
  return (
    deal?.sales_dashboard_created_deal_by_label ||
    deal?.created_by_admin_name ||
    deal?.created_by_admin_email ||
    deal?.created_by_admin?.email ||
    deal?.created_deal_by ||
    '-'
  )
}

const findOptionLabel = (options = [], id) => {
  const row = (options || []).find((x) => String(x.id) === String(id))
  if (!row) return '-'
  return row.code ? `${row.code} - ${row.name}` : row.name
}

const BuyingPoSourcingPage = () => {
  const { id } = useParams()

  const [loading, setLoading] = useState(false)
  const [dealData, setDealData] = useState(null)
  const [meta, setMeta] = useState({ designers: [], categories: [], conditions: [] })
  const [error, setError] = useState(null)
  const [actionMsg, setActionMsg] = useState(null)
  const [cancelSkuReason, setCancelSkuReason] = useState('')
  const [cancellingSku, setCancellingSku] = useState(false)
  const [showCancelSkuForm, setShowCancelSkuForm] = useState(false)

  // ✅ Static list (mirip Sales deal_by)
  const purchasingNameList = [
    { label: 'Jacqueline - JANE', value: 'Jacqueline' },
    { label: 'Mifta - KARLA', value: 'Mifta' },
    { label: 'Dewi - ELLA', value: 'Dewi' },
    { label: 'Tiara - LISA', value: 'Tiara' },
    { label: 'Sofia - ALMA', value: 'Sofia' },
    { label: 'Lika - LUNA', value: 'Lika' },
    { label: 'Anggi - KARIN', value: 'Anggi' },
    { label: 'Cindy - ZARA', value: 'Cindy' },
    { label: 'Others', value: 'Others' },
  ]

  const load = async () => {
    setLoading(true)
    setError(null)

    // 1) Always load deal first (critical path)
    try {
      const dealRes = await SalesDealsApi.buyingPoSourcingDetail(id)
      const data = dealRes?.data?.data || null

      setDealData(data)
    } catch (e) {
      console.error('Failed load deal:', e)
      setDealData(null)
      setError(e?.response?.data?.message || e?.message || 'Failed load deal')
      setLoading(false)
      return
    }

    // 2) Load meta (non-blocking)
    try {
      const metaRes = await SalesDealsApi.buyingProductMeta()
      setMeta(
        metaRes?.data?.data || {
          designers: [],
          categories: [],
          conditions: [],
        }
      )
    } catch (e) {
      console.warn('Failed load product meta (non-blocking):', e)
      setMeta({
        designers: [],
        categories: [],
        conditions: [],
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const deal = dealData?.deal
  const placeholderItem = dealData?.placeholder_item
  const placeholderNote = placeholderItem?.note || ''
  // NOTE:
  // Setelah deal jadi sourced, item yang ditampilkan bukan lagi placeholder PO,
  // tapi product hasil sourcing. Karena itu kita simpan reference product aktif di sini.
  const trackedProduct = placeholderItem?.product
  const replacementPending = Boolean(dealData?.replacement_pending)
  const cancelledProduct = dealData?.cancelled_product
  const isBrandCategoryLocked = !replacementPending && Boolean(trackedProduct?.designer_id && trackedProduct?.category_id)
  const lockedDesignerLabel = findOptionLabel(meta.designers, trackedProduct?.designer_id)
  const lockedCategoryLabel = findOptionLabel(meta.categories, trackedProduct?.category_id)

  // ✅ Robust final price fallback (locked selling price from deal/cart item)
  const customerFinalPrice = useMemo(() => {
    return (
      dealData?.customer_final_price ||
      placeholderItem?.customer_final_price ||
      placeholderItem?.selling_price ||
      placeholderItem?.price ||
      ''
    )
  }, [dealData, placeholderItem])

  const canStartSourcing = deal?.state === 'dp_confirmed'
  // NOTE:
  // BT harus tetap bisa edit detail sourcing walaupun state sudah sourced,
  // karena kasus barang belum datang / detail masih perlu diperbaiki.
  const canSourced = ['on_sources', 'sourced'].includes(deal?.state)
  const canCancelSku = deal?.state === 'sourced' && String(trackedProduct?.sku || '').toUpperCase().startsWith('BKO-')


  const handleStartSourcing = async () => {
    setActionMsg(null)
    setError(null)
    try {
      await SalesDealsApi.buyingStartSourcing(id, { notes: '' })
      setActionMsg('✅ Deal moved to on_sources')
      await load()
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed start sourcing')
    }
  }

  const closeReplaceSkuModal = () => {
    if (cancellingSku) return

    setCancelSkuReason('')
    setShowCancelSkuForm(false)
  }

  const handleCancelSku = async () => {
    const reason = cancelSkuReason.trim()

    setActionMsg(null)
    setError(null)
    setCancellingSku(true)

    try {
      const res = await SalesDealsApi.buyingCancelSourcedProduct(id, {
        product_id: trackedProduct?.id,
        reason,
      })
      setCancelSkuReason('')
      setShowCancelSkuForm(false)
      setActionMsg(
        res?.data?.message ||
          '✅ SKU lama berhasil diarsipkan. Silakan isi ulang spec replacement product.'
      )
      await load()
    } catch (e) {
      setError(e?.response?.data?.message || 'Gagal mengganti sourced SKU')
    } finally {
      setCancellingSku(false)
    }
  }

  return (
    <>
      <TitlePage mainTitle={`Buying — PO Sourcing #${deal?.reference_number || id}`} />

      <Card body>
        {loading && <Alert color="secondary">Loading...</Alert>}
        {error && <Alert color="danger">{error}</Alert>}
        {actionMsg && <Alert color="success">{actionMsg}</Alert>}

        {deal && (
          <Alert color="info">
            <div>
              <b>Ref:</b> {deal.reference_number}
            </div>
            <div>
              <b>State:</b> {deal.state}
            </div>
            <div>
              <b>DP:</b> {deal.down_payment || 0}
            </div>
            <div>
              <b>Created By:</b> {getCreatedByText(deal)}
            </div>
            <div>
              <b>Sourced By:</b> {deal.sourced_by || '-'}
            </div>

            <div style={{ marginTop: 8 }}>
              <RedirectButton text="Open Deal Detail" path={`/app/sales/deals/${deal.id}`} />
            </div>
          </Alert>
        )}

        {/* NOTE: */}
        {/* Saat state sourced, product yang tampil adalah product hasil sourcing, */}
        {/* jadi jangan hardcode lagi sebagai placeholder product. */}

        {trackedProduct && (
          <Alert color="light">
            <div>
              <b>Product:</b> {trackedProduct.sku} — {trackedProduct.name}
            </div>
          </Alert>
        )}

        {replacementPending && (
          <Alert color="warning">
            <div><b>Waiting Replacement Product</b></div>
            <div style={{ marginTop: 6 }}>
              Cancelled SKU: <b>{cancelledProduct?.sku || '-'}</b>
              {cancelledProduct?.name ? ` — ${cancelledProduct.name}` : ''}
            </div>
            {cancelledProduct?.deletion_note && (
              <div style={{ marginTop: 4 }}>Reason: {cancelledProduct.deletion_note}</div>
            )}
          </Alert>
        )}

        <Alert color={customerFinalPrice ? 'light' : 'warning'}>
          <div style={{ fontWeight: 700 }}>
            {replacementPending ? 'Current Final Price (editable pada form replacement)' : 'Selling Price (locked from Deal/DP)'}
          </div>
          <div style={{ marginTop: 6 }}>
            {customerFinalPrice ? (
              <span>{customerFinalPrice}</span>
            ) : (
              <span>❌ Selling price tidak ditemukan dari deal/cart item. Tidak bisa lanjut sourced.</span>
            )}
          </div>
        </Alert>

        <h4>PO Detail dari Sales (free text)</h4>
        {placeholderNote ? (
          <Alert color="light" style={{ whiteSpace: 'pre-wrap' }}>
            {placeholderNote}
          </Alert>
        ) : (
          <FormText className="text-muted">Tidak ada note PO.</FormText>
        )}

        <hr />

        {placeholderItem?.po_condition_id && (
          <Alert color="light">
            <div><b>Condition (SA):</b></div>
            <div style={{ marginTop: 6 }}>
              {
                meta.conditions.find(c => c.id === Number(placeholderItem.po_condition_id))?.name
                || 'Unknown'
              }
            </div>
          </Alert>
        )}

        {placeholderItem?.po_comes_with && (
          <Alert color="light">
            <div><b>Kelengkapan (Comes With) dari SA:</b></div>
            <div style={{ whiteSpace: 'pre-wrap', marginTop: 6 }}>
              {placeholderItem.po_comes_with}
            </div>
          </Alert>
        )}


        <div style={{ marginTop: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
              padding: '12px 14px',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              background: '#ffffff',
            }}
          >
            <div style={{ minWidth: 220 }}>
              {canCancelSku ? (
                <>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>
                    SKU sourcing aktif
                  </div>
                  <div style={{ fontWeight: 700, color: '#374151' }}>
                    {trackedProduct?.sku || '-'}
                  </div>
                </>
              ) : canStartSourcing ? (
                <>
                  <div style={{ fontWeight: 700, color: '#374151' }}>Sourcing belum dimulai</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                    Mulai sourcing setelah DP sudah dikonfirmasi.
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  Gunakan Refresh untuk memuat status Deal terbaru.
                </div>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Button color="secondary" outline size="sm" disabled={loading} onClick={load}>
                Refresh
              </Button>

              {canStartSourcing && (
                <Button color="primary" size="sm" disabled={loading} onClick={handleStartSourcing}>
                  Start Sourcing
                </Button>
              )}

              {canCancelSku && (
                <Button
                  color="danger"
                  outline
                  size="sm"
                  disabled={loading || cancellingSku}
                  onClick={() => {
                    setError(null)
                    setActionMsg(null)
                    setCancelSkuReason('')
                    setShowCancelSkuForm(true)
                  }}
                >
                  Ganti SKU
                </Button>
              )}
            </div>
          </div>

          <Modal
            isOpen={showCancelSkuForm}
            toggle={cancellingSku ? undefined : closeReplaceSkuModal}
            centered
          >
            <ModalHeader
              toggle={cancellingSku ? undefined : closeReplaceSkuModal}
              style={{ justifyContent: 'center' }}
            >
              Apakah Anda yakin untuk mengganti SKU?
            </ModalHeader>

            <ModalBody style={{ textAlign: 'center', padding: '32px 48px' }}>
              <div
                style={{
                  fontSize: 18,
                  lineHeight: 1.5,
                  color: '#536184',
                  marginBottom: 20,
                }}
              >
                SKU ini akan diarsipkan, Deal tetap aktif dan form spesifikasi akan
                dibuka kembali untuk membuat SKU BKO pengganti.
              </div>

              <div
                style={{
                  fontSize: 18,
                  color: '#8a99b2',
                  marginBottom: 24,
                }}
              >
                <strong>SKU:</strong> {trackedProduct?.sku || '-'}
              </div>

              <Input
                id="replace-sku-reason"
                type="textarea"
                rows={4}
                value={cancelSkuReason}
                onChange={(event) => setCancelSkuReason(event.target.value)}
                placeholder="Alasan penggantian (opsional)"
                disabled={cancellingSku || loading}
                style={{ textAlign: 'left' }}
              />
            </ModalBody>

            <ModalFooter style={{ justifyContent: 'center', gap: 12 }}>
              <Button
                type="button"
                color="secondary"
                outline
                disabled={cancellingSku}
                onClick={closeReplaceSkuModal}
              >
                Cancel
              </Button>

              <Button
                type="button"
                color="danger"
                disabled={cancellingSku || loading}
                onClick={handleCancelSku}
              >
                {cancellingSku ? 'Memproses...' : 'Yes'}
              </Button>
            </ModalFooter>
          </Modal>
        </div>

        {!canSourced && (
          <Alert color="warning" style={{ marginTop: 12 }}>
            Untuk input detail & create product, deal harus state <b>on_sources</b>.
            {deal?.state === 'dp_confirmed' ? (
              <>
                {' '}
                Klik <b>Start Sourcing</b> dulu.
              </>
            ) : null}
          </Alert>
        )}

        <hr />

        {canSourced && (
          <Alert color="secondary">
            {replacementPending ? (
              <>
                Isi ulang seluruh spec product dan Final Price. Submit akan membuat <b>SKU BKO baru</b> lalu otomatis attach ke Deal yang sama.
              </>
            ) : (
              <>
                PO sourcing sekarang selalu menggunakan <b>Create New BKO</b>.
                Attach existing SKU sudah dinonaktifkan agar Deal PO tidak terhubung ke SKU non-BKO.
              </>
            )}
          </Alert>
        )}

        {canSourced && (
          <>
            <h4>Input Detail Product (Buying Team)</h4>

            <Formik 
          enableReinitialize
          initialValues={{
            sourced_by: replacementPending ? '' : (deal?.sourced_by || ''),
            // Replacement harus diisi ulang dari awal oleh Buying Team.
            seller_user_id: replacementPending ? '' : (trackedProduct?.seller_user_id || ''),
            seller_user_detail_id: replacementPending ? '' : (trackedProduct?.seller_user_detail_id || ''),
            supplier_name: replacementPending ? '' : (trackedProduct?.supplier_name || ''),
            designer_id: replacementPending ? '' : (trackedProduct?.designer_id || ''),
            category_id: replacementPending ? '' : (trackedProduct?.category_id || ''),
            condition_id: replacementPending ? '' : (trackedProduct?.condition_id || placeholderItem?.po_condition_id || ''),
            name: replacementPending ? '' : (trackedProduct?.name || ''),
            p_size: replacementPending ? '' : (trackedProduct?.p_size || ''),
            buying_price: replacementPending ? '' : (trackedProduct?.buying_price || ''),
            comes_with: replacementPending ? '' : (trackedProduct?.comes_with || ''),
            selling_price: customerFinalPrice || '',
            notes: '',
          }}
          validate={(v) => {
            const errors = {}
            if (!canSourced) return errors

            if (!(v.sourced_by || '').trim()) errors.sourced_by = 'Sourced by wajib dipilih'

            if (!v.seller_user_id || !v.seller_user_detail_id) errors.seller_user_detail_id = 'Pilih Seller Detail terdaftar'
            if (!(v.supplier_name || '').trim()) errors.supplier_name = 'Supplier name wajib'
            if (!toIntOrNull(v.designer_id)) errors.designer_id = 'Designer wajib'
            if (!toIntOrNull(v.category_id)) errors.category_id = 'Category wajib'
            if (replacementPending && !toIntOrNull(v.condition_id)) errors.condition_id = 'Condition wajib'
            if (replacementPending && !(v.comes_with || '').trim()) errors.comes_with = 'Kelengkapan / Comes With wajib'
            if (!(v.name || '').trim()) errors.name = 'Product name wajib'

            const bp = toNumberOrNull(v.buying_price)
            if (bp === null || bp <= 0) errors.buying_price = 'Buying price wajib > 0'

            const sp = toNumberOrNull(v.selling_price)
            if (sp === null || sp <= 0) errors.selling_price = 'Selling price (deal) tidak ditemukan'

            return errors
          }}
          onSubmit={async (values, formikBag) => {
            setError(null)
            setActionMsg(null)

            try {
              const payload = {
                sourced_by: values.sourced_by,
                final_price: values.selling_price,
                product: {
                  supplier_name: values.supplier_name,
                  seller_user_id: values.seller_user_id,
                  seller_user_detail_id: values.seller_user_detail_id,
                  designer_id: values.designer_id,
                  category_id: values.category_id,
                  condition_id: values.condition_id || null,
                  name: values.name,
                  p_size: values.p_size || null,
                  buying_price: values.buying_price,
                  comes_with: values.comes_with || null,
                },
                notes: values.notes || '',
              }

              const res = await SalesDealsApi.buyingSourced(id, payload)
              const sku = res?.data?.data?.new_product_sku
              // NOTE:
              // Success message dibuat generic karena endpoint sekarang bisa
              // dipakai untuk create pertama kali maupun update setelah sourced.
              const syncStatus = res?.data?.data?.settlement_sync
              const syncMessage = syncStatus === 'completed'
                ? ' Settlement sudah disinkronkan.'
                : syncStatus ? ' Sourcing tersimpan; sinkronisasi Settlement menunggu retry. Gunakan button Sinkronisasi Sekarang bila belum muncul.' : ''
              setActionMsg(`${res?.data?.message || '✅ Saved'}${sku ? `. SKU: ${sku}` : ''}${syncMessage}`)

              formikBag.setSubmitting(false)
              await load()
            } catch (e) {
              setError(e?.response?.data?.message || 'Failed sourced')
              formikBag.setSubmitting(false)
            }
          }}
        >
          {(formikBag) => (
            <Form
              onSubmit={(e) => e.preventDefault()}
              style={{
                opacity: canSourced ? 1 : 0.6,
                pointerEvents: canSourced ? 'auto' : 'none',
              }}
            >
              <InputField type="select" name="sourced_by" label="Sourced By (Buyer)" required>
                <option value="">-- pilih buyer --</option>
                {purchasingNameList.map((x) => (
                  <option key={x.value} value={x.value}>
                    {x.label}
                  </option>
                ))}
              </InputField>

              <SourcingSellerPicker value={formikBag.values.seller_user_detail_id} label={formikBag.values.supplier_name} onChange={detail => {
                formikBag.setFieldValue('seller_user_id', detail?.user_id || '')
                formikBag.setFieldValue('seller_user_detail_id', detail?.user_detail_id || '')
                formikBag.setFieldValue('supplier_name', detail?.name || '')
              }} />
              {formikBag.errors.seller_user_detail_id && <div className='text-danger'>{formikBag.errors.seller_user_detail_id}</div>}

              {isBrandCategoryLocked && (
                <Alert color="info">
                  Brand & Category dikunci dari input Sales: <b>{lockedDesignerLabel}</b> / <b>{lockedCategoryLabel}</b>.
                </Alert>
              )}

              <InputField
                type="select"
                name="designer_id"
                label="Designer"
                required
                disabled={isBrandCategoryLocked}
                formText={isBrandCategoryLocked ? 'Locked dari Sales Assistant. Tidak bisa diubah oleh Buying Team.' : undefined}
              >
                <option value="">-- pilih designer --</option>
                {(meta.designers || []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} ({d.code})
                  </option>
                ))}
              </InputField>

              <InputField
                type="select"
                name="category_id"
                label="Category"
                required
                disabled={isBrandCategoryLocked}
                formText={isBrandCategoryLocked ? 'Locked dari Sales Assistant. Tidak bisa diubah oleh Buying Team.' : undefined}
              >
                <option value="">-- pilih category --</option>
                {(meta.categories || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.code})
                  </option>
                ))}
              </InputField>

              {replacementPending ? (
                <InputField type="select" name="condition_id" label="Condition" required>
                  <option value="">-- pilih condition --</option>
                  {(meta.conditions || []).map((condition) => (
                    <option key={condition.id} value={condition.id}>
                      {condition.name}
                    </option>
                  ))}
                </InputField>
              ) : (
                <InputField
                  type="text"
                  name="condition_id_display"
                  label="Condition (From Sales)"
                  disabled
                  value={
                    meta.conditions.find(c =>
                      c.id === Number(placeholderItem?.po_condition_id || trackedProduct?.condition_id)
                    )?.name || "-"
                  }
                  formText="Condition ditentukan oleh Sales saat create PO. Tidak bisa diubah."
                />
              )}


              <InputField
                type="text"
                name="name"
                label="Product Name"
                required
                placeholder="Contoh: Kelly 28 Retourne"
              />

              <InputField
                type="text"
                name="p_size"
                label="Spec / Size (opsional)"
                placeholder="Contoh: 28 / Gold Hardware"
              />

              <InputField
                type="textarea"
                name="comes_with"
                label={replacementPending ? 'Comes With / Kelengkapan' : 'Comes With / Kelengkapan (opsional)'}
                required={replacementPending}
                placeholder="Contoh: Box, dust bag, strap"
              />


              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <ThousandsInputField name="buying_price" label="Buying Price (HPP) — input Buying" required />
                </div>

                <div style={{ flex: 1, minWidth: 240 }}>
                  <ThousandsInputField
                    name="selling_price"
                    label={replacementPending ? 'Final Price Baru' : 'Selling Price (Harga di Deal/DP awal) — read-only'}
                    disabled={!replacementPending}
                    required={replacementPending}
                    formText={
                      replacementPending
                        ? 'Final Price akan memperbarui harga cart item, total Deal, dan sisa payment.'
                        : 'Harga ini dikunci dari Sales saat DP dibuat. Tidak boleh diubah.'
                    }
                  />
                </div>
              </div>

              <Alert color="secondary" style={{ marginTop: 12 }}>
                SKU akan <b>digenerate otomatis</b> oleh sistem.
                {replacementPending ? ' Existing SKU tidak akan digunakan kembali.' : ''}
              </Alert>

              <InputField type="text" name="notes" label="Notes (opsional)" placeholder="Catatan buying" />

              <Button
                color="success"
                // NOTE:
                // Tombol submit tetap aktif selama state masih on_sources / sourced.
                disabled={formikBag.isSubmitting || !canSourced || !toNumberOrNull(formikBag.values.selling_price)}
                onClick={async () => {
                  const errors = await formikBag.validateForm()
                  if (Object.keys(errors).length > 0) {
                    alert(JSON.stringify(errors, null, 2))
                    return
                  }
                  formikBag.submitForm()
                }}
              >
                {replacementPending
                  ? 'Create Replacement BKO & Attach to Deal'
                  : deal?.state === 'sourced'
                    ? 'Save Sourced Product Detail'
                    : 'Mark as Sourced (Create Product + Swap Placeholder)'}
              </Button>
            </Form>
          )}
            </Formik>
          </>
        )}
      </Card>
    </>
  )
}

export default BuyingPoSourcingPage
